// Galerie plein écran des réalisations et références techniques
// - Chargement différé (lazy) pour préserver la vitesse
// - Ouverture en lightbox plein écran, navigation clavier (←/→/Esc)
// - Légendes et alt trilingues (FR / EN / PT)
// - Aucune donnée confidentielle affichée
import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { REALISATIONS } from "@/data/realisations";
import { useT, useLocalized } from "@/i18n/helpers";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";

export default function RealisationsGallery({ compact = false }) {
  const tt = useT();
  const localized = useLocalized(REALISATIONS);
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(
    () => setActive((i) => (i === null ? null : (i - 1 + localized.length) % localized.length)),
    [localized.length]
  );
  const next = useCallback(
    () => setActive((i) => (i === null ? null : (i + 1) % localized.length)),
    [localized.length]
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active, close, prev, next]);

  const items = compact ? localized.slice(0, 3) : localized;
  const current = active !== null ? localized[active] : null;

  return (
    <div data-testid="realisations-gallery">
      <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {items.map((r, i) => (
          <StaggerItem key={r.slug}>
            <button
              type="button"
              onClick={() => setActive(i)}
              data-testid={`realisation-thumb-${r.slug}`}
              className="group relative block w-full aspect-[4/3] overflow-hidden border border-nanda-line bg-nanda-bgAlt focus:outline-none focus:ring-2 focus:ring-nanda-terracotta"
              aria-label={r.caption}
            >
              <img
                src={r.src}
                alt={r.alt}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-nanda-ink/80 via-nanda-ink/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end text-left">
                <div className="label-overline text-white/80 mb-2 flex items-center gap-2">
                  <span className="tabular-nums">0{i + 1}</span>
                  <Maximize2 size={14} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="font-serif text-white text-lg md:text-xl leading-snug">
                  {r.caption}
                </div>
              </div>
            </button>
          </StaggerItem>
        ))}
      </Stagger>

      {/* Lightbox */}
      {current && (
        <div
          className="fixed inset-0 z-[100] bg-nanda-ink/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10"
          onClick={close}
          data-testid="realisation-lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors p-2"
            aria-label={tt("Fermer", "Close", "Fechar")}
            data-testid="realisation-close"
          >
            <X size={28} strokeWidth={1.5} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors p-3 bg-black/30 rounded-full"
            aria-label={tt("Image précédente", "Previous image", "Imagem anterior")}
            data-testid="realisation-prev"
          >
            <ChevronLeft size={28} strokeWidth={1.5} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors p-3 bg-black/30 rounded-full"
            aria-label={tt("Image suivante", "Next image", "Próxima imagem")}
            data-testid="realisation-next"
          >
            <ChevronRight size={28} strokeWidth={1.5} />
          </button>

          <div
            className="relative max-w-6xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={current.src}
              alt={current.alt}
              className="w-full h-auto max-h-[70vh] object-contain bg-black"
            />
            <div className="mt-4 md:mt-6 px-1 md:px-2">
              <div className="label-overline text-white/60 mb-2 tabular-nums">
                {String(active + 1).padStart(2, "0")} / {String(localized.length).padStart(2, "0")}
              </div>
              <div className="font-serif text-white text-xl md:text-2xl leading-snug">
                {current.caption}
              </div>
              <p className="mt-3 text-white/70 text-sm md:text-base leading-relaxed max-w-3xl">
                {current.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
